import { useEffect, useState, useRef } from "react";
import { useAuth } from "@/lib/AuthContext";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useLang } from "@/lib/LanguageContext";

export default function NotificationBell() {
  const { user } = useAuth();
  const { t } = useLang();
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const intervalRef = useRef(null);

  const fetchUnread = async () => {
    try {
      const { data } = await api.get("/notifications/unread-count");
      setCount(data.count);
    } catch {}
  };

  const fetchItems = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data);
    } catch {}
  };

  useEffect(() => {
    if (!user) { setCount(0); setItems([]); return; }
    fetchUnread();
    fetchItems();
    const poll = () => { if (document.visibilityState === "visible") fetchUnread(); };
    intervalRef.current = setInterval(poll, 15000);
    const onVisChange = () => { if (document.visibilityState === "visible") fetchUnread(); };
    document.addEventListener("visibilitychange", onVisChange);
    return () => { clearInterval(intervalRef.current); document.removeEventListener("visibilitychange", onVisChange); };
    // eslint-disable-next-line
  }, [user]);

  const markAllRead = async () => {
    await api.post("/notifications/read-all");
    setCount(0);
    fetchItems();
  };

  if (!user) return null;

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) fetchItems(); }}>
      <PopoverTrigger asChild>
        <button
          data-testid="notification-bell"
          className="relative w-9 h-9 rounded-full hover:bg-muted flex items-center justify-center transition-colors"
        >
          <Bell className="w-5 h-5" />
          {count > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-destructive text-destructive-foreground text-[10px] font-bold rounded-full flex items-center justify-center" data-testid="notification-badge">
              {count > 9 ? "9+" : count}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between p-3 border-b border-border">
          <h3 className="font-semibold text-sm">{t("common.notifications")}</h3>
          {count > 0 && (
            <button onClick={markAllRead} className="text-xs text-primary hover:underline flex items-center gap-1" data-testid="mark-all-read-btn">
              <CheckCheck className="w-3.5 h-3.5" /> {t("common.mark_all_read")}
            </button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">{t("common.no_notifications")}</p>
          ) : (
            items.slice(0, 10).map(n => (
              <Link
                to={n.job_id ? `/jobs/${n.job_id}` : "/dashboard"}
                key={n.id}
                onClick={() => setOpen(false)}
                className={`block px-3 py-2.5 hover:bg-muted transition-colors border-b border-border last:border-0 ${!n.is_read ? "bg-primary/5" : ""}`}
              >
                <p className="text-sm font-medium leading-tight">{n.title}</p>
                <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{n.message}</p>
                <p className="text-[10px] text-muted-foreground mt-1">{new Date(n.created_at).toLocaleString("da-DK", { dateStyle: "short", timeStyle: "short" })}</p>
              </Link>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
